import accounting from 'accounting'
import parseFullName from 'parse-full-name'
import React from 'react'

import { LANGUAGE, NUMBER_WORDS, LEAD_AVAILABILITY_DATE_TIME_WINDOW_LABELS } from 'common/constants'
import moment from "moment-timezone";

export const get = (value, defaultValue) => value ? value : defaultValue

export const defaultValue = (options, value) => value ? value : options[0].value

export const labelFor = (options, value) => {
  if (!value) return 'N/A'

  let item = options.find(item => item.value == value)
  if (!item) return 'N/A'

  return item.label
}

export const propertyFor = (options, value, x) => {
  if (!value) return 'N/A'

  let item = options.find(item => item.value == value)
  if (!item) return 'N/A'

  return item[x]
}

export const hasLabel = (options, value) => {
  return labelFor(options, value) !== 'N/A'
}

export const randomString = (length) => {
  var text = "";
  var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for(var i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

export const coalesce = (...args) => {
  for (let i = 0; i < args.length; i++) {
    if (args[i] !== null && args[i] !== undefined) {
      return args[i]
    }
  }
  return null
}

export const intersperse = (a, f) => {
  var pairs = _.zip(
    _.tail(a),
    _.slice(a, 0, -1)
  )

  var inner = _.map(pairs, _.isFunction(f) ? _.spread(f) : _.constant(f))

  var result = _.chain(a)
    .zip(inner)
    .flatten()
    .slice(0, -1)
    .value()

  return result
}

export const sortOptionsMap = (options, array) => {
  let result = []
  options.map((item) => {
    if (_.indexOf(array, item.label) >= 0) {
      result.push(item.label)
    }
  })
  return result
}

export const objectFieldComparator = (fieldName) => {
  return (a, b) => {
    return a[fieldName].localeCompare(b[fieldName])
  }
}

export const newlineToBreak = (value, setAsHTML = true) => {
  return value !== null ? value.split("\n").map(function(item, idx) {
    return (
      <span key={idx}>
          { setAsHTML ? <span dangerouslySetInnerHTML={{__html: item}}/> : <span>{`${item}`}</span>}
        <br/>
      </span>
    )
  }) : null
}

export const excerpt = (value, length=300) => {
  if (value) {
    if (value.length < length) {
      return value
    }
    let excerpt = value.substring(0, length)
    return excerpt.substring(0, excerpt.lastIndexOf(' ')) + '…'
  }
  return null
}

export const digitsOnly = (number) => {
  return number.replace(/[^0-9]/g, '');
}

export const isDigitsOnly = (number) => {
  return /^\d+$/.test(number);
}

export const isValidPhone = (number)  => {
  if (!number) {
    return false
  }
  let digits = digitsOnly(number);
  return digits.length >= 10 && digits.length <= 11
}

export const isValidEmail = (email) => {
  if (!email) {
    return false
  }
  email = email.trim()
  const re = /\S+@\S+\.\S+/
  return email && email.indexOf(' ') < 0 ? email.match(re) : false
}

export const isValidZipcode = (zipcode)  => {
  if (!zipcode) {
    return false
  }

  return zipcode.length == 5 && /^\d+$/.test(zipcode)
}

export const isValidName = (name) => {
  if (!name) {
    return false
  }

  name = name.trim()
  return /\S+/.test(name)
}

export const combineDateAndTimeStr = (date, timeStr) => {
  return new Date(date.getFullYear() + '-' + (date.getMonth()+1) + '-' + date.getDate() + ' ' + timeStr + ':00', )
}

export const getLocalJSDatetime = (tzOffset) => {
  const MIN_MILLISECS = 60000
  const offsetMins = parseInt(tzOffset) / 100 * 60
  return new Date(new Date().getTime() + (MIN_MILLISECS * offsetMins))
}

export const convertToLocal = (timeValue, localOffset) => {
  const MIN_MILLISECS = 60000
  const localOffsetMins = parseInt(localOffset) / 100 * 60
  return new Date(new Date(timeValue).getTime() - (MIN_MILLISECS * localOffsetMins))
}

const padTime = (timeInt) => {
  return ((timeInt < 10) ? "0" : "") + timeInt
}

export const secondsToTimecode = (seconds) => {
  let hours = Math.floor(seconds / (60 * 60))
  seconds %= (60 * 60)
  let minutes = Math.floor(seconds / 60)
  seconds %= 60

  let timeString = ":" + padTime(seconds)
  if (hours) {
    timeString = hours + ":" + padTime(minutes) + timeString
  } else {
    timeString = minutes + timeString
  }
  return timeString
}

export const secondsToWords = (seconds) => {
  let hours = Math.floor(seconds / (60 * 60))
  seconds %= (60 * 60)
  let minutes = Math.floor(seconds / 60)
  seconds %= 60

  let timeString = ""
  if (hours) {
    timeString = hours + (hours === 1 ? " hour, " : " hours, ")
  }
  if (minutes) {
    timeString = timeString + minutes + (minutes === 1 ? " minute, " : " minutes, ")
  }
  return timeString + seconds + (seconds === 1 ? " second" : " seconds")
}

export const getElapsedTimecode = (startTime) => {
  let now = Date.now()
  let differenceMs = (now - startTime)
  let differenceSeconds = Math.floor(differenceMs / 1000)
  return secondsToTimecode(differenceSeconds)
}

export const getBlobExtension = (blob) => {
  if (blob) {
    let type = blob.type
    if (type) {
      type = type.toLowerCase()
      if (type.endsWith('png')) {
        return '.png'
      } else if (type.endsWith('jpg') ||
          type.endsWith('jpeg')) {
        return '.jpg'
      } else if (type.endsWith('gif')) {
        return '.gif'
      } else if (type.endsWith('bmp')) {
        return '.bmp'
      } else if (type.endsWith('tiff')) {
        return '.tiff'
      }
    }
  }
  return '.png'
}

export const getClosingStatementFilename = (transactionName, agentName, brokerName, closeDate,
                                            originalFilename=null, external_transaction_id=null) => {
  const date = new Date()
  const dateStr = closeDate || `${date.getFullYear()}.${date.getMonth()+1}.${date.getDate()}`
  const hash = randomString(6)
  const extension = originalFilename ? originalFilename.split('.').pop().toLowerCase() : 'pdf'
  const filenameString = `${dateStr}_${transactionName}_${agentName}_${brokerName}_${hash}`
    + (external_transaction_id ? `_${external_transaction_id}` : ``)
    + `.${extension}`

  // encode URI to get rid of special characters (especially '/'), then put spaces back in, they're ok
  const encoded = encodeURIComponent(filenameString)
  return encoded.replace(/%20/g, ' ')
}

export const prettyShortNumber = (number, isCurrency=false) => {
  if(!number) {
    return '—'
  }

  let fixedNum, postFix
  if(number > 1000000000) {
    fixedNum = (number/1000000000).toFixed(1)
    postFix = 'b'
  } else if(number > 1000000) {
    fixedNum = (number/1000000).toFixed(1)
    postFix = 'm'
  } else if(number >= 1000) {
    fixedNum = (number/1000).toFixed(0)
    postFix = 'k'
  } else {
    fixedNum = number.toFixed(2)
    postFix = ''
  }
  let currency = isCurrency ? '$' : ''
  return `${currency}${fixedNum}${postFix}`
}

export const numberToCurrency = (num = 0) => {
  let str
  // check for cents
  if (num % 1 === 0) {
    str = num.toString()
  } else {
    str = num.toFixed(2)
  }
  return `$${str.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
}

export const formatInt = (num) => {
  if(typeof(num) === 'undefined') {
    return null
  }
  return num.toLocaleString()
}

export const toFixedPretty = (value, precision) => {
  return accounting.toFixed(value, precision).replace(/[.,]0+$/, "")
}

export const stripHtmlExceptLinks = (value) => {
  // very naive html stripping to leave only links
  value = value.replace(/<[aA] /gi, "%%%---a").replace(/<\/[aA]>/gi, "%%%%----a")
  value = value.replace(/<(?:.|\s)*?>/g, "")
  value = value.replace(/%%%---a/gi, "<a ").replace(/%%%%----a/gi, "</a>")
  return value
}

export const convertNewlinesToBreaks = (value) => {
  return value.replace(/(?:\r\n|\r|\n)/g, "<br/>")
}

export const getHtmlTextLength = (html) => {
  let tmp = document.createElement("div")
  tmp.innerHTML = html
  return (tmp.textContent || tmp.innerText || "").length
}

export const isTimeSinceWithin24Hours = (timeSince) => {
  return timeSince.includes('hour') || timeSince.includes('minute') || timeSince.includes('second')
}

export const formatDateTime = (date) => {
  let timeAgo = moment(date).startOf('hour').fromNow()
  date = moment(date).local()
  if (timeAgo.includes('second') || timeAgo.includes('minute') || timeAgo.includes('hour')) {
    return date.format('h:mm A (') + timeAgo + ')'
  } else if (timeAgo.includes('day') || timeAgo.includes('month')) {
    return date.format('MMM D (') + timeAgo + ')'
  } else if (timeAgo.includes('year')) {
    return date.format('ll (') + timeAgo + ')'
  }
}

export const minutesFromNow = (date) => moment.utc().diff(moment(date), 'minutes')

export const getFirstAndLastName = (name) => {
  const declinedRegex = / Declined last name$/
  if(name&&name.match(declinedRegex)){
    return { 'first_name': name.split(declinedRegex)[0], 'last_name': 'Declined last name' }
  }
  const parsedName = parseFullName.parseFullName(name, 'all', false)
  return parsedName ? {'first_name': parsedName.first, 'last_name': parsedName.last} : {'first_name': null, 'last_name': null}
}

export const titleCase = (str) => {
  const word = []
  for (let char of str.split(' ')) {
      word.push(char[0].toUpperCase() + char.slice(1))
  }

  return word.join(' ')
}

export const brandToCardClass = (brand) => {
  const brandString = brand ? brand.replace(/ /g,'').toLowerCase() : null
  const brands = {
    'americanexpress': "fa fa-cc-amex",
    'visa': "fa fa-cc-visa",
    'mastercard': "fa fa-cc-mastercard",
    'discover': "fa fa-cc-discover",
    'bank': "fa fa-university"
  }

  return brands[brandString] ? brands[brandString] : "fa fa-credit-card"
}

export const calcPercentThroughMonth = () => {
  const now = new Date()
  const beginningOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth()+1, 1);
  return (now - beginningOfMonth) / (endOfMonth - beginningOfMonth)*100
}

export const leadUseLanguageString = lead => {
  let language_name = null
  if (lead.last_call_log_spanish) {
    language_name = labelFor(LANGUAGE, 'spanish')
  } else {
    if (lead.agent_language_preference && lead.agent_language_preference !== 'no-preference') {
      language_name = labelFor(LANGUAGE, lead.agent_language_preference)
    }
  }
  return language_name
}

export const lastTwoDigits = num => {
  const str = num.toString()
  return str.substring(str.length - 2)
}

export const CSVToArray = (strData) => {
  const objPattern = new RegExp(("(\\,|\\r?\\n|\\r|^)(?:\"([^\"]*(?:\"\"[^\"]*)*)\"|([^\\,\\r\\n]*))"),"gi");
  let arrMatches = objPattern.exec(strData), arrData = [[]]
  while (arrMatches) {
      if (arrMatches[1].length && arrMatches[1] !== ",") {
        arrData.push([])
      }
      arrData[arrData.length - 1].push(arrMatches[2] ? arrMatches[2].replace(new RegExp( "\"\"", "g" ), "\"") : arrMatches[3])
      arrMatches = objPattern.exec(strData)
  }
  return arrData.filter(row => row.filter(c => c).length)
}

export const wordForNumber = num => {
  return num < NUMBER_WORDS.length ? NUMBER_WORDS[num] : 0
}

export const boolToYesOrNo = value => {
  return value ? "Yes" : "No"
}

export const leadHasFirstLastName = lead => lead.name.indexOf(' ') !== -1 && lead.name.indexOf('@') === -1 && lead.name !== 'Inbound Caller'

export const goToLink = (openLinkOnMobile, url) => {
  if (openLinkOnMobile) {
    window.ReactNativeWebView.postMessage(`open-url:${url}`)
  } else {
    window.open(url, '_blank')
  }
}

export const formatUnreportedCloseAddress = (unreportedCloseData) => {
  if(!unreportedCloseData) return
  let street_address = unreportedCloseData.property_street_address
  let unit_number = unreportedCloseData.property_unit_number
  let city = unreportedCloseData.property_city
  let state = unreportedCloseData.property_state
  let postal_code = unreportedCloseData.property_postal_code
  let formattedAddress = 'N/A'
  if(street_address && city && state && postal_code) {
    let end = [state, postal_code].join(' ')
    let start = ''
    if(unit_number){
      // remove unit number if already appended to last part of the address
      let possibleUnitIndex = street_address.lastIndexOf(" " + unit_number)
      if(possibleUnitIndex != -1) {
        street_address = street_address.substring(0, possibleUnitIndex)
      }
      start = [street_address, unit_number, city].join(', ')
    } else {
      start = [street_address, city].join(', ')
    }
    formattedAddress = [start.toLowerCase(), end].join(', ')
  }
  return formattedAddress
}

export const getOrderedFoundTransactionsForAssignment = (assignmentId, transactionInfo) => {
  return Object.values(transactionInfo)
    .filter((transaction)=>transaction.assignment_id === assignmentId)
    .sort((a,b)=> {
      if (a.confident_match_id && a.id === a.confident_match_id) {
        return -1
      }

      if(b.confident_match_id && b.id === b.confident_match_id){
        return 1
      }

      return b.created_at>a.created_at ? 1 : -1
    })
}

export const getHumanReadableLeadAvailability = ({ date, date_range, time, time_range }) => {
  let readableDate, readableTime

  if (date_range == 'by-appointment') {
    return labelFor(LEAD_AVAILABILITY_DATE_TIME_WINDOW_LABELS, date_range)
  }

  if (time_range == 'asap') {
    return labelFor(LEAD_AVAILABILITY_DATE_TIME_WINDOW_LABELS, time_range)
  }

  if (!date) {
    readableDate = labelFor(LEAD_AVAILABILITY_DATE_TIME_WINDOW_LABELS, date_range)
  } else {
    readableDate = date
  }

  if (!time) {
    if (time_range == 'anytime') {
      readableDate = `${readableDate}s`
    }

    readableTime = labelFor(LEAD_AVAILABILITY_DATE_TIME_WINDOW_LABELS, time_range)
  } else {
    readableDate = `${readableDate}s at`
    readableTime = time
  }

  return `${readableDate} ${readableTime}`
}

export const getTransactionTitleCompany = (transaction) => {
  if(transaction.qualia_enabled && transaction.selected_settlement_provider){
    const title_provider_name = transaction.selected_settlement_provider.settlement_provider_name
    return title_provider_name
  }
  else if(transaction.agent_close_title_company){
    return transaction.agent_close_title_company
  }
  else{
    return transaction.settlement_opt_out_company
  }
}
